import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Avatar from '../components/common/Avatar';
import ContributorCard from '../components/blog/ContributorCard';
import api from '../utils/api';
import { formatDate, truncateText } from '../utils/helpers';
import toast from 'react-hot-toast';

// Scroll reveal hook - re-runs when deps change (e.g. articles loaded)
function useScrollReveal(deps = []) {
  const ref = useRef(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    container.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, deps);

  return ref;
}

// Featured article card (first article, larger)
const FeaturedArticleCard = ({ article }) => (
  <Link
    to={`/article/${article.slug}`}
    className="reveal group relative bg-surface rounded-2xl overflow-hidden card-hover border border-transparent hover:border-yellow-400/20 col-span-1 md:col-span-2 flex flex-col md:flex-row h-full"
  >
    {/* Image */}
    <div className="relative md:w-3/5 h-56 md:h-auto overflow-hidden flex-shrink-0">
      {article.featured_image ? (
        <img
          src={article.featured_image}
          alt={article.title}
          loading="eager"
          decoding="async"
          fetchpriority="high"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center min-h-[280px]">
          <i className="fas fa-newspaper text-6xl text-gray-900 opacity-50"></i>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-surface/80 hidden md:block"></div>
      {article.category_name && (
        <div
          className="absolute top-4 left-4 px-3 py-1 rounded-md text-xs font-semibold text-gray-900 tracking-wider uppercase"
          style={{ backgroundColor: article.category_color }}
        >
          {article.category_name}
        </div>
      )}
    </div>

    {/* Content */}
    <div className="p-6 md:p-8 flex flex-col justify-center flex-grow">
      <span className="eyebrow mb-3">À la une</span>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors leading-tight">
        {article.title}
      </h2>
      <p className="text-gray-400 text-sm md:text-base mb-6 line-clamp-3 leading-relaxed">
        {truncateText(article.short_description, 200)}
      </p>

      <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-4 border-t border-gray-700/20">
        <div className="flex items-center space-x-3">
          <Avatar
            user={{ username: article.author_name, avatar: article.author_avatar }}
            size="sm"
            showName={true}
          />
          {article.reading_time && (
            <span><i className="fas fa-clock mr-1"></i>{article.reading_time} min</span>
          )}
        </div>
        <span>{formatDate(article.published_at)}</span>
      </div>
    </div>
  </Link>
);

// Standard article card
const ArticleCard = ({ article }) => (
  <Link
    to={`/article/${article.slug}`}
    className="reveal group bg-surface rounded-xl overflow-hidden card-hover border border-transparent hover:border-yellow-400/10 flex flex-col h-full"
  >
    {/* Image */}
    <div className="relative h-44 overflow-hidden flex-shrink-0">
      {article.featured_image ? (
        <img
          src={article.featured_image}
          alt={article.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
          <i className="fas fa-newspaper text-5xl text-gray-900 opacity-50"></i>
        </div>
      )}
      {article.category_name && (
        <div
          className="absolute top-3 left-3 px-2.5 py-0.5 rounded-md text-xs font-semibold text-gray-900 tracking-wider uppercase"
          style={{ backgroundColor: article.category_color }}
        >
          {article.category_name}
        </div>
      )}
    </div>

    {/* Content */}
    <div className="p-5 flex flex-col flex-grow">
      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors line-clamp-2 min-h-[3rem] leading-snug">
        {article.title}
      </h3>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2 flex-grow leading-relaxed">
        {truncateText(article.short_description, 100)}
      </p>

      <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-3 border-t border-gray-700/20">
        <div className="flex items-center space-x-3">
          <Avatar
            user={{ username: article.author_name, avatar: article.author_avatar }}
            size="sm"
            showName={true}
          />
          {article.reading_time && (
            <span><i className="fas fa-clock mr-1"></i>{article.reading_time} min</span>
          )}
        </div>
        <span>{formatDate(article.published_at)}</span>
      </div>
    </div>
  </Link>
);

const PAGE_SIZE = 9;

const BlogHome = () => {
  const { categorySlug } = useParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || null);
  const gridRef = useScrollReveal([articles]);
  const sentinelRef = useRef(null);

  // Ref miroir pour éviter les fetch concurrents déclenchés par l'IntersectionObserver
  const isFetchingRef = useRef(false);

  useEffect(() => {
    loadCategories();
  }, []);

  // Reset + premier chargement à chaque changement de catégorie
  useEffect(() => {
    setArticles([]);
    setTotal(0);
    loadArticles(0, true);
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  const loadArticles = useCallback(async (offset, isFirstPage) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      if (isFirstPage) setLoading(true);
      else setLoadingMore(true);

      const params = { limit: PAGE_SIZE, offset };
      if (selectedCategory) params.category = selectedCategory;

      const response = await api.get('/articles', { params });
      if (response.data.success) {
        setArticles((prev) => (isFirstPage ? response.data.articles : [...prev, ...response.data.articles]));
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Erreur chargement articles:', error);
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [selectedCategory]);

  // Infinite scroll via IntersectionObserver sur la sentinelle en bas de grille
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (articles.length === 0) return;
    if (articles.length >= total) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingRef.current) {
          loadArticles(articles.length, false);
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [articles.length, total, loadArticles]);

  const [featured, ...rest] = articles;
  const hasMore = articles.length > 0 && articles.length < total;

  return (
    <>
      <Helmet>
        <title>Blog SunServ - Actualités et Guides Hébergement Gaming</title>
        <meta name="description" content="Découvrez nos articles, tutoriels et actualités sur l'hébergement gaming. Minecraft, FiveM, VPS et plus encore." />
      </Helmet>

      <Header />

      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Hero Section */}
          <div className="relative mb-16 py-16 overflow-hidden">
            {/* Ambient glow */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-yellow-400/[0.04] rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10 max-w-3xl">
              <span className="eyebrow mb-4">Hébergement Gaming</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
                Le blog<br /><span className="text-yellow-400">SunServ</span>
              </h1>
              <p className="text-gray-400 text-lg md:text-xl max-w-xl leading-relaxed">
                Actualités, guides et tutoriels pour les passionnés d'hébergement gaming.
              </p>
            </div>
          </div>

          {/* Contributor CTA */}
          <div className="mb-16 max-w-5xl mx-auto">
            <ContributorCard />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                !selectedCategory
                  ? 'bg-yellow-400 text-gray-900'
                  : 'bg-surface text-gray-400 hover:text-white hover:bg-surface-raised'
              }`}
            >
              <i className="fas fa-th-large mr-2"></i>
              Tous
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.slug)}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedCategory === category.slug
                    ? 'text-gray-900'
                    : 'bg-surface text-gray-400 hover:text-white hover:bg-surface-raised'
                }`}
                style={{
                  backgroundColor: selectedCategory === category.slug ? category.color : undefined,
                }}
              >
                {category.icon && <i className={`fas fa-${category.icon} mr-1.5`}></i>}
                {category.name}
              </button>
            ))}
          </div>

          {/* Articles Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-10 h-10 border-3 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-500 mt-4 text-sm">Chargement...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <i className="fas fa-folder-open text-5xl text-gray-700 mb-4"></i>
              <p className="text-gray-500 text-lg">Aucun article dans cette catégorie</p>
            </div>
          ) : (
            <div ref={gridRef} className="reveal-stagger">
              {/* Featured + grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {featured && <FeaturedArticleCard article={featured} />}
              </div>

              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              )}

              {/* Sentinelle infinite scroll + indicateur de chargement */}
              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-10">
                  {loadingMore && (
                    <div className="inline-block w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default BlogHome;
